import React, { ForwardedRef, SyntheticEvent, forwardRef, memo, useEffect, useState } from 'react'
import { IconButton, Tooltip, Popover, Dialog, DialogContent, DialogActions, PopoverProps } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import ResponsiveButton from 'components/ResponsiveButton'
import Button from 'components/Button'
import Box from 'components/Box'
import Condition from 'components/Condition'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import { makeStyles } from 'tss-react/mui'
import { useResponsiveBreakpoint } from 'hooks/useResponsiveBreakpoint'

export interface IMoreButtonCardChildrenProps {
  close: (e: SyntheticEvent) => void
}

interface IMoreButtonCardProps {
  disabled?: boolean
  popoverStyle?: React.CSSProperties
  PopoverProps?: Omit<PopoverProps, 'open' | 'anchorEl' | 'onClose'>
  responsive?: boolean
  iconOnly?: boolean
  mobileLabel?: string
  mobileDialogCloseLabel?: React.ReactNode
  label?: string
  tooltip?: string
  icon?: any
  component?: any
  buttonChildren?: any
  color?: 'primary' | 'secondary' | 'error'
  onOpen?: () => void
  onClose?: () => void
  style?: React.CSSProperties
  className?: string
  size?: 'large' | 'medium' | 'small'
  breakpoint?: number
  children?: React.ReactNode | ((props: IMoreButtonCardChildrenProps) => React.ReactNode)
}

const useStyles = makeStyles()({
  popoverContent: {
    maxWidth: 400,
  },
})

// TODO: clean up code
function MoreButtonCard(props: IMoreButtonCardProps, ref: ForwardedRef<HTMLButtonElement>) {
  const {
    responsive,
    mobileLabel,
    label,
    tooltip,
    disabled,
    icon = <MoreHorizIcon />,
    size = 'large',
    component,
    buttonChildren,
    popoverStyle,
    children,
    onClose,
    onOpen,
    mobileDialogCloseLabel,
    PopoverProps,
    ...otherProps
  } = props
  const { classes } = useStyles()
  const theme = useTheme()

  const [isOpen, setOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>(null)

  useEffect(() => {
    if (disabled && isOpen) {
      setOpen(false)
    }
  }, [disabled, isOpen])

  const handleButtonClick = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()

    setAnchorEl(e.currentTarget)
    setOpen(true)

    if (onOpen) onOpen()
  }

  const handlePopoverClose = (e?: any) => {
    e?.stopPropagation()

    setAnchorEl(e.currentTarget)
    setOpen(false)

    if (onClose) onClose()
  }

  const childrenElement = typeof children === 'function' ? (
    children({ close: handlePopoverClose })
  ) : children

  let buttonElement = null

  if (component) {
    const Component = component

    buttonElement = (
      <Component
        ref={ref}
        onClick={handleButtonClick}
        label={label}
        icon={icon}
        tooltip={tooltip}
        disabled={disabled}
        {...otherProps}
      >
        {buttonChildren}
      </Component>
    )
  } else if (responsive) {
    buttonElement = (
      <ResponsiveButton
        {...otherProps}
        ref={ref}
        onClick={handleButtonClick}
        label={label}
        mobileLabel={mobileLabel}
        icon={icon}
        tooltip={tooltip}
        disabled={disabled}
      />
    )
  } else if (tooltip && !disabled) {
    buttonElement = (
      <Tooltip title={tooltip}>
        <IconButton {...otherProps} ref={ref} onClick={handleButtonClick} size={size}>
          {icon}
        </IconButton>
      </Tooltip>
    )
  } else {
    buttonElement = (
      <IconButton
        {...otherProps}
        ref={ref}
        onClick={handleButtonClick}
        disabled={disabled}
        size={size}
      >
        {icon}
      </IconButton>
    )
  }

  const isMobile = useResponsiveBreakpoint(theme.breakpoints.values.sm)

  return (
    <React.Fragment>
      {buttonElement}

      {(isOpen && !disabled) && (
      <React.Fragment>
        <Condition condition={isMobile}>
          <Dialog fullScreen open onClose={handlePopoverClose}>
            <DialogContent>
              {childrenElement}
            </DialogContent>

            <DialogActions>
              <Button onClick={handlePopoverClose}>
                {mobileDialogCloseLabel || 'Close'}
              </Button>
            </DialogActions>
          </Dialog>
        </Condition>

        <Condition condition={!isMobile}>
          <Popover
            {...PopoverProps}
            open
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
          >
            <Box
              className={classes.popoverContent}
              style={popoverStyle}
              data-testid="more-button-card"
            >
              {childrenElement}
            </Box>
          </Popover>
        </Condition>
      </React.Fragment>
      )}
    </React.Fragment>
  )
}

export default memo(forwardRef(MoreButtonCard))
